import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';

/**
 * BACK RETURNS THE READER TO THE VIEW THEY LEFT (founder, 2026-09-13).
 *
 * "Active bakeries: I click on a bakery to see its details, back should bring
 * me back to the exact same view and scrolling position in the table." Three
 * things stood between a staff portal and that sentence, and this module is
 * the one answer to all three, for the partner, bakery and internal portals:
 *
 *  1. THE ARROW WENT TO A FIXED PLACE. Every record page's arrow was a link to
 *     its list's bare URL - `/organizations/bakeries/mode-a/shops` for a shop -
 *     so a reader who reached a shop from Active bakeries, or from an order,
 *     was sent to a different tab from the one they came from. The arrow now
 *     walks the tab's own history back to where the reader WAS
 *     (`historyStepsBack`), and only falls back to the list's URL when there is
 *     no in-app page behind this one: a deep link pasted into a fresh tab.
 *
 *  2. WHAT THE READER HAD SET WAS REACT STATE. A filter, a search, a sort, a
 *     page, an expanded row - all of it lived in `useState` on a screen that
 *     unmounts the moment a row is opened, so "back" rebuilt the list from its
 *     defaults. `useEntryState` is `useState` whose value is kept PER HISTORY
 *     ENTRY: the list re-mounted by a back step reads what that entry held; the
 *     same list opened fresh from the rail is a new entry and starts clean,
 *     which is what a reader who clicks "Bakeries" again is asking for.
 *
 *  3. THE BROWSER CANNOT RESTORE A SCROLL IT NEVER SAW. Its own restoration
 *     runs when the popstate lands, a frame before React has drawn the rows
 *     back, and it knows nothing about a table that scrolls inside its own box
 *     (`.cib-tablewrap--fill`, which is exactly where Active bakeries scrolls).
 *     So restoration is switched to `manual` and done here: positions are taken
 *     when an entry is LEFT and put back, frame by frame, once the rows are tall
 *     enough to hold them - and abandoned the moment the reader touches
 *     anything, because a page that yanks itself back under a moving thumb is
 *     worse than one that forgot.
 *
 * HOW AN ENTRY IS KNOWN. `pushState` and `replaceState` are wrapped once, at
 * startup (`installHistoryMemory`, from each portal's `main.tsx`), to stamp a
 * key and a position on the entry's state; wouter passes `null` there, so the
 * stamp is the entry's whole state and nothing a screen reads changes. A replace
 * keeps the entry's stamp - swapping `?property=` in place is the same view - and
 * a push mints the next one. A per-tab TRAIL (position -> path) is what lets the
 * arrow skip the entries that belong to the record it is leaving.
 *
 * Kept in `sessionStorage`, so a reload keeps its place too, and never in
 * `localStorage`: a second tab is a second trail, and yesterday's filters are
 * not what anyone reopens a portal for. Everything degrades: blocked storage
 * forgets across a reload and nothing else; a test or a static render that
 * never installs this gets plain `useState` and plain links.
 */

const STORE_KEY = 'cib:history-memory';
/** The stamp's property on `history.state`. */
const MARK = '__cib';
/** Entries kept, most recently visited first. A session of clicking stays small. */
const ENTRY_CAP = 150;
/**
 * The boxes whose scroll is kept, besides the window. The shell's main column
 * and every table wrap are shared; a portal marks one of its own with
 * `data-scroll-memory` (the counter's `bk-main`).
 */
export const SCROLL_MEMORY_SELECTOR = '.cib-shell__main, .cib-tablewrap, [data-scroll-memory]';
/**
 * How long a restore keeps trying to reach its position. Long enough for a list
 * that has to be refetched through Hyperdrive; the reader's first touch ends it
 * sooner.
 */
const RESTORE_PATIENCE_MS = 5_000;

interface EntryMark {
  readonly key: string;
  /** Position in this tab's history, counted from the first page of this app. */
  readonly idx: number;
}

/** `target` -1 is the window; otherwise the index among SCROLL_MEMORY_SELECTOR's matches. */
export interface ScrollMark {
  readonly target: number;
  readonly top: number;
  readonly left: number;
}

interface EntryRecord {
  path: string;
  at: number;
  scroll: ScrollMark[];
  state: Record<string, unknown>;
}

interface Memory {
  entries: Record<string, EntryRecord>;
  /** trail[idx] is the path of this tab's entry at that position. */
  trail: string[];
}

/**
 * The two things scroll handling needs from a document, as a port: the DOM
 * version below, or a test's fake - jsdom lays nothing out, so it cannot
 * scroll anything.
 */
export interface ScrollPort {
  /** Every box currently scrolled away from its origin. */
  read(): ScrollMark[];
  /**
   * Put the marks back; any box not named goes to its origin. True once every
   * mark was reached, which is what ends a restore early.
   */
  apply(marks: readonly ScrollMark[]): boolean;
}

interface Installed {
  current: EntryMark;
  memory: Memory;
  scroll: ScrollPort;
  listeners: Set<() => void>;
  cancelRestore: (() => void) | null;
  flushQueued: boolean;
  uninstall: () => void;
}

let installed: Installed | null = null;

function mintKey(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function readMark(state: unknown): EntryMark | null {
  if (state === null || typeof state !== 'object') return null;
  const mark = (state as Record<string, unknown>)[MARK];
  if (mark === null || typeof mark !== 'object') return null;
  const { key, idx } = mark as Record<string, unknown>;
  return typeof key === 'string' && typeof idx === 'number' && idx >= 0 ? { key, idx } : null;
}

/**
 * The state with the stamp on it. Only `null` and plain objects are stamped: a
 * primitive or an array cannot carry a property without changing what the app
 * reads back, and nothing here passes one.
 */
function withMark(state: unknown, mark: EntryMark): unknown {
  if (state === null || state === undefined) return { [MARK]: mark };
  if (typeof state === 'object' && !Array.isArray(state)) return { ...state, [MARK]: mark };
  return state;
}

function pathOf(url: string | URL | null | undefined): string {
  if (url === null || url === undefined) return location.pathname;
  try {
    return new URL(String(url), location.href).pathname;
  } catch {
    return location.pathname;
  }
}

function loadMemory(): Memory {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Memory>;
      if (parsed && typeof parsed.entries === 'object' && Array.isArray(parsed.trail)) {
        return { entries: parsed.entries ?? {}, trail: parsed.trail };
      }
    }
  } catch {
    /* Blocked or corrupt storage: start with nothing remembered. */
  }
  return { entries: {}, trail: [] };
}

function flush(self: Installed): void {
  self.flushQueued = false;
  const entries = Object.entries(self.memory.entries);
  if (entries.length > ENTRY_CAP) {
    entries.sort(([, a], [, b]) => b.at - a.at);
    self.memory.entries = Object.fromEntries(entries.slice(0, ENTRY_CAP));
  }
  try {
    globalThis.sessionStorage?.setItem(STORE_KEY, JSON.stringify(self.memory));
  } catch {
    /* Full or denied: the tab still remembers until it is reloaded. */
  }
}

function queueFlush(self: Installed): void {
  if (self.flushQueued) return;
  self.flushQueued = true;
  queueMicrotask(() => flush(self));
}

function recordFor(self: Installed, key: string, path: string): EntryRecord {
  const existing = self.memory.entries[key];
  if (existing !== undefined) {
    existing.at = Date.now();
    return existing;
  }
  const created: EntryRecord = { path, at: Date.now(), scroll: [], state: {} };
  self.memory.entries[key] = created;
  return created;
}

function enter(self: Installed, mark: EntryMark, path: string): void {
  self.current = mark;
  // A push from the middle of the trail discards everything after it, as the
  // browser just discarded those entries.
  self.memory.trail.length = Math.min(self.memory.trail.length, mark.idx);
  self.memory.trail[mark.idx] = path;
  recordFor(self, mark.key, path).path = path;
  queueFlush(self);
  for (const listener of self.listeners) listener();
}

function saveScroll(self: Installed): void {
  const record = recordFor(self, self.current.key, location.pathname);
  record.scroll = self.scroll.read();
  queueFlush(self);
}

function cancelRestore(self: Installed): void {
  self.cancelRestore?.();
  self.cancelRestore = null;
}

/**
 * Put an entry's scroll back once the page can hold it.
 *
 * The first attempt waits a frame: the popstate that brought the reader here
 * fires before React has rendered the page it names. Each later frame tries
 * again, because the rows may still be arriving; the first touch, wheel or key
 * ends it, and so does the next navigation.
 */
function restoreScroll(self: Installed, marks: readonly ScrollMark[]): void {
  cancelRestore(self);
  const started = Date.now();
  let cancelNext = (): void => {};
  const stop = (): void => {
    cancelNext();
    for (const type of STOP_EVENTS) removeEventListener(type, stop, true);
    if (self.cancelRestore === stop) self.cancelRestore = null;
  };
  const step = (): void => {
    const reached = self.scroll.apply(marks);
    if (reached || Date.now() - started > RESTORE_PATIENCE_MS) {
      stop();
      return;
    }
    cancelNext = nextAttempt(step);
  };
  for (const type of STOP_EVENTS) addEventListener(type, stop, { capture: true, passive: true });
  self.cancelRestore = stop;
  cancelNext = nextAttempt(step);
}

/**
 * The next attempt: on the next frame while the tab is visible, on a short
 * timer while it is not. A hidden tab runs no animation frames at all, so a
 * restore scheduled on frames alone waited for the tab to be shown - and a page
 * restored in a background tab (a reload, a Back pressed from a script) never
 * was.
 */
function nextAttempt(callback: () => void): () => void {
  if (typeof document !== 'undefined' && document.hidden) {
    const timer = setTimeout(callback, 50);
    return () => clearTimeout(timer);
  }
  const frame = requestAnimationFrame(callback);
  return () => cancelAnimationFrame(frame);
}

const STOP_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/** The DOM's own scroll port: the window plus every box SCROLL_MEMORY_SELECTOR names. */
export const domScrollPort: ScrollPort = {
  read() {
    const marks: ScrollMark[] = [];
    if (scrollX !== 0 || scrollY !== 0) marks.push({ target: -1, top: scrollY, left: scrollX });
    document.querySelectorAll(SCROLL_MEMORY_SELECTOR).forEach((box, target) => {
      if (box.scrollTop !== 0 || box.scrollLeft !== 0) {
        marks.push({ target, top: box.scrollTop, left: box.scrollLeft });
      }
    });
    return marks;
  },
  apply(marks) {
    const boxes = document.querySelectorAll(SCROLL_MEMORY_SELECTOR);
    let reached = true;
    const reach = (top: number, left: number, now: () => [number, number]): void => {
      const [y, x] = now();
      if (Math.abs(y - top) > 1 || Math.abs(x - left) > 1) reached = false;
    };
    const win = marks.find((mark) => mark.target === -1);
    const winTop = win?.top ?? 0;
    const winLeft = win?.left ?? 0;
    if (scrollY !== winTop || scrollX !== winLeft) scrollTo(winLeft, winTop);
    reach(winTop, winLeft, () => [scrollY, scrollX]);
    boxes.forEach((box, target) => {
      const mark = marks.find((candidate) => candidate.target === target);
      const top = mark?.top ?? 0;
      const left = mark?.left ?? 0;
      if (box.scrollTop !== top) box.scrollTop = top;
      if (box.scrollLeft !== left) box.scrollLeft = left;
      reach(top, left, () => [box.scrollTop, box.scrollLeft]);
    });
    // A marked box the page has not drawn yet is a position not yet reached.
    if (marks.some((mark) => mark.target >= boxes.length)) reached = false;
    return reached;
  },
};

export interface HistoryMemoryOptions {
  /** A test's scroll double. The DOM's own by default. */
  readonly scroll?: ScrollPort;
}

/**
 * Start remembering. Call once, before the first render, in a portal's
 * `main.tsx`. A second call is a no-op; the returned function undoes the
 * install (for tests).
 */
export function installHistoryMemory(options: HistoryMemoryOptions = {}): () => void {
  if (installed !== null) return installed.uninstall;
  if (typeof history === 'undefined' || typeof location === 'undefined') return () => {};

  const originalPush = history.pushState;
  const originalReplace = history.replaceState;
  const memory = loadMemory();

  let mark = readMark(history.state);
  // A document with no stamp is a fresh start of this app in this tab: whatever
  // the trail held belongs to a page this one did not follow.
  const fresh = mark === null;
  if (mark === null) {
    mark = { key: mintKey(), idx: 0 };
    originalReplace.call(history, withMark(history.state, mark), '', location.href);
  }

  const self: Installed = {
    current: mark,
    memory,
    scroll: options.scroll ?? domScrollPort,
    listeners: new Set(),
    cancelRestore: null,
    flushQueued: false,
    uninstall: () => {},
  };
  if (fresh) memory.trail.length = 0;

  try {
    history.scrollRestoration = 'manual';
  } catch {
    /* Read-only in some embedded browsers; the page simply restores less. */
  }

  history.pushState = function pushState(
    state: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    cancelRestore(self);
    saveScroll(self);
    const next: EntryMark = { key: mintKey(), idx: self.current.idx + 1 };
    const before = location.pathname;
    enter(self, next, pathOf(url));
    const result = originalPush.call(this, withMark(state, next), unused, url);
    // A new page starts at its top; a push that only changes the query string
    // is the same page, and stays where the reader is.
    if (location.pathname !== before) self.scroll.apply([]);
    return result;
  };

  history.replaceState = function replaceState(
    state: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    const path = pathOf(url);
    const result = originalReplace.call(this, withMark(state, self.current), unused, url);
    if (self.memory.trail[self.current.idx] !== path) enter(self, self.current, path);
    return result;
  };

  const onPopState = (event: PopStateEvent): void => {
    // The page being left is still on screen: this listener runs before React
    // renders the entry the browser has already moved to.
    saveScroll(self);
    const arrived = readMark(event.state);
    if (arrived === null) {
      // An entry the browser made itself - a skip link's `#main`. It is a new
      // entry after this one; stamp it and leave its scroll to the browser.
      const next: EntryMark = { key: mintKey(), idx: self.current.idx + 1 };
      originalReplace.call(history, withMark(event.state, next), '', location.href);
      enter(self, next, location.pathname);
      return;
    }
    enter(self, arrived, location.pathname);
    restoreScroll(self, self.memory.entries[arrived.key]?.scroll ?? []);
  };

  const onPageHide = (): void => {
    saveScroll(self);
    flush(self);
  };

  addEventListener('popstate', onPopState);
  addEventListener('pagehide', onPageHide);

  self.uninstall = () => {
    cancelRestore(self);
    removeEventListener('popstate', onPopState);
    removeEventListener('pagehide', onPageHide);
    history.pushState = originalPush;
    history.replaceState = originalReplace;
    if (installed === self) installed = null;
  };
  installed = self;

  enter(self, mark, location.pathname);
  // A reload: put the page back where the reader had it.
  const saved = memory.entries[mark.key]?.scroll ?? [];
  if (!fresh && saved.length > 0) restoreScroll(self, saved);
  return self.uninstall;
}

/**
 * How many steps back the nearest page OUTSIDE `scope` is, or 0 when there is
 * no such page in this tab's in-app history - a deep link, a new tab.
 *
 * `scope` is the path prefix every page of the record shares, so a record whose
 * tabs are routes (`/menu/:itemId/:tab`) is left in one click however many of
 * its tabs the reader opened. Without one, only entries at this exact path are
 * skipped: a query-string push on the same page.
 */
export function historyStepsBack(scope?: string): number {
  const self = installed;
  if (self === null) return 0;
  const here = self.memory.trail[self.current.idx] ?? location.pathname;
  const inScope = (path: string): boolean =>
    scope === undefined ? path === here : path === scope || path.startsWith(`${scope}/`);
  for (let idx = self.current.idx - 1; idx >= 0; idx -= 1) {
    const path = self.memory.trail[idx];
    if (path === undefined) return 0;
    if (!inScope(path)) return self.current.idx - idx;
  }
  return 0;
}

const NOT_INSTALLED = '';

function subscribe(listener: () => void): () => void {
  const self = installed;
  if (self === null) return () => {};
  self.listeners.add(listener);
  return () => self.listeners.delete(listener);
}

/** Which entry, at which path, a screen is drawing right now. */
function currentSlot(): string {
  const self = installed;
  if (self === null) return NOT_INSTALLED;
  return `${self.current.key}\n${self.memory.trail[self.current.idx] ?? ''}`;
}

function serverSlot(): string {
  return NOT_INSTALLED;
}

function slotRecord(slot: string): EntryRecord | undefined {
  const self = installed;
  if (self === null || slot === NOT_INSTALLED) return undefined;
  return self.memory.entries[slot.slice(0, slot.indexOf('\n'))];
}

function stateId(slot: string, id: string): string {
  return `${slot.slice(slot.indexOf('\n') + 1)}|${id}`;
}

function readEntryValue<T>(slot: string, id: string): { readonly value: T } | undefined {
  const record = slotRecord(slot);
  const name = stateId(slot, id);
  if (record === undefined || !Object.hasOwn(record.state, name)) return undefined;
  return { value: record.state[name] as T };
}

function writeEntryValue(slot: string, id: string, value: unknown): void {
  const self = installed;
  const record = slotRecord(slot);
  if (self === null || record === undefined) return;
  // Round-tripped now rather than at the flush, so the value read back within
  // the visit is the value a reload would read: a Set kept here would come
  // back as `{}` after F5 and as a Set before it.
  record.state[stateId(slot, id)] =
    value === undefined ? undefined : JSON.parse(JSON.stringify(value));
  queueFlush(self);
}

/**
 * `useState`, kept per history entry. The value must survive JSON; a function
 * cannot be one (it would read as an updater).
 *
 * `id` names the value within its screen - `'mode'`, `'devices:<shop id>'` -
 * and is scoped by the entry AND the path, so two screens that both call a
 * filter `status` never read each other's.
 *
 * On a back step to an entry that kept a value, the screen gets that value.
 * When the entry changes under a screen that stays mounted - a push that only
 * moved the query string - the value it already had carries on: that is the
 * same view continuing, not a new one.
 */
export function useEntryState<T>(
  id: string,
  initial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const slot = useSyncExternalStore(subscribe, currentSlot, serverSlot);
  const [held, setHeld] = useState<{ readonly slot: string; readonly value: T }>(() => {
    const stored = readEntryValue<T>(slot, id);
    return {
      slot,
      value:
        stored !== undefined
          ? stored.value
          : typeof initial === 'function'
            ? (initial as () => T)()
            : initial,
    };
  });

  let value = held.value;
  if (held.slot !== slot) {
    const stored = readEntryValue<T>(slot, id);
    value = stored !== undefined ? stored.value : held.value;
    // Adjusted during the render that notices, as `useTable` adjusts its page:
    // nothing paints the previous entry's value first.
    setHeld({ slot, value });
  }

  useEffect(() => {
    writeEntryValue(slot, id, value);
  }, [slot, id, value]);

  const set = useCallback<Dispatch<SetStateAction<T>>>((action) => {
    setHeld((previous) => ({
      slot: previous.slot,
      value: typeof action === 'function' ? (action as (prior: T) => T)(previous.value) : action,
    }));
  }, []);

  return [value, set];
}
